import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { create, useStore } from "zustand";
import React, { useEffect, useState } from "react";
import { WebShopIcon } from "@/src/components/WebShopIcon";
import { useTimer } from "react-timer-hook";
import { isMobile } from "react-device-detect";
import { DiiaButton } from "@/src/components/DiiaSharing/DiiaButton";

export const documentTypeLabels = {
  INTERNAL_PASSPORT: "Паспорт громадянина України",
  VEHICLE_LICENSE: "Свідоцтво про реєстрацію транспортного засобу",
  FOREIGN_PASSPORT: "Закордонний паспорт громадянина України",
  DRIVER_LICENSE: "Права на керування транспортним засобом",
};

export const DEFAULT_EXPIRED_TIME = 60 * 3;

/**
 * Get timestamp of expired time
 * @param sec – seconds to expired
 * @returns – timestamp
 * @example getExpiryTimestamp(60) // 60 seconds from now
 */
export const getExpiryTimestamp = (
  sec = Number(DEFAULT_EXPIRED_TIME),
): Date => {
  const time = new Date();
  time.setSeconds(time.getSeconds() + sec);
  return time;
};

/**
 * Format time to 00:00
 * @param time – time in seconds
 * @returns – formatted time
 * @example formatTime(5) // 05
 */
export const formatTime = (time: number): string | number => {
  return time < 10 ? `0${time}` : time;
};

function TimerComponent({
  onReset,
  ready,
}: {
  onReset?: () => void;
  ready: boolean;
}) {
  const { seconds, minutes, isRunning, start, restart } = useTimer({
    expiryTimestamp: getExpiryTimestamp(),
    autoStart: false,
  });

  useEffect(() => {
    if (ready) {
      start();
    }
  }, [ready]);
  return (
    <Box className={"flex cursor-pointer items-center gap-1 text-blue"}>
      {isRunning ? (
        `${formatTime(minutes)}:${formatTime(seconds)}`
      ) : (
        <>
          <WebShopIcon name="refresh-ccw" />
          <Typography
            className="text-blue"
            onClick={() => {
              onReset?.();
              restart(getExpiryTimestamp());
            }}
          >
            Оновити QR-код
          </Typography>
        </>
      )}
    </Box>
  );
}

export const DiiaModal = ({
  loading,
  url,
  open,
  handleClose,
  handleDiiaSharing,
  diiaButtonProps,
  scopes,
}: any) => {
  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="flex w-full max-w-[524px] flex-col gap-6 rounded-xl bg-white p-6 shadow-xl center-modal max-md:animate-up-modal max-md:bottom-modal">
        <Stack className="w-full flex-center-row">
          <Typography
            variant="h1"
            component="h3"
            className="flex-1 text-center text-2xl font-semibold"
          >
            {"Заповнення даних через Дію"}
          </Typography>
          <IconButton onClick={handleClose}>
            {" "}
            <WebShopIcon name="close" className="fill-blue" />
          </IconButton>
        </Stack>
        <Stack className="flex flex-col gap-4 text-base text-dark-gray">
          <Typography variant={"body1"}>
            {
              "Під час використання сервісу Дія будуть автоматично передані цифрові копії  таких документів:"
            }
          </Typography>
          <ol className="list-inside list-disc">
            {scopes.map((scope: string) => (
              <li key={scope}>
                {documentTypeLabels[scope as keyof typeof documentTypeLabels]}
              </li>
            ))}
          </ol>
          {!isMobile ? (
            <ol className="list-inside list-decimal">
              <li>Відкрийте застосунок Дія на вашому смартфоні.</li>
              <li>Переконайтесь, що у вас активовано Дія.Підпис.</li>
              <li>На головному екрані натисніть кнопку сканування QR-коду.</li>
              <li>Відскануйте QR-код на цьому екрані.</li>
            </ol>
          ) : (
            <ol className="list-inside list-decimal">
              <li>Для переходу в застосунок Дія натисніть кнопку нижче.</li>
              <li>Переконайтесь, що у вас активовано Дія.Підпис.</li>
              <li>Дотримуйтесь подальших інструкцій в застосунку Дія.</li>
            </ol>
          )}
          <Box className={"flex flex-col items-center justify-center gap-3"}>
            <Box className={"relative flex items-center justify-center"}>
              {(!url || loading) && (
                <Box
                  className={
                    "absolute flex h-full w-full items-center justify-center bg-super-light-blue opacity-70"
                  }
                />
              )}
              {(!url || loading) && (
                <CircularProgress className={"absolute"} color={"primary"} />
              )}
              {!isMobile && <QRCodeSVG value={url || ""} size={248} />}
              {isMobile && <DiiaButton {...diiaButtonProps} />}
            </Box>
            <TimerComponent onReset={handleDiiaSharing} ready={!!url} />
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};
